import { forwardRef,ReactElement } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Box, Flex, MEDIA_QUERY, Text, useResponsive } from '@bucketplace/design-system';
import { IconPlayFilled } from '@bucketplace/icons';

import { LIFE_EVENT_TEXT } from 'brand/common/style';
import { LifeEventCardType } from 'brand/types';

import { LIFE_EVENT_IMAGE_SIZE } from '../constants';
import { VideoPreview } from '../VideoPreview';
import { ModalButtonWrapper } from '../ModalButtonWrapper';
import { bucketClickLog } from '@bucketplace/common';

export const Card = forwardRef<
HTMLDivElement,
LifeEventCardType & { index: number }
>(({ thumbnail, text, url, rotate, index }, ref): ReactElement => {
  const { breakpoint } = useResponsive({});
  // const { mobile, laptop } = LIFE_EVENT_IMAGE_SIZE;
  const isMobile = breakpoint === 'mobile' || breakpoint === 'tablet';

  return (
    <Box
      position="absolute"
      top="50%"
      left="50%"
      ref={ref}
      bdr={24}
      h="100%"
      w="100%"
      overflow="hidden"
      css={{
        transform: `translate3d(-50%, -50%, 0) rotate(${rotate}deg)`,
        pointerEvents: 'none',
        willChange: 'transform, opacity',
        isolation: 'isolate',
      }}
    >
      <Video
        src={thumbnail}
        autoPlay
        muted
        loop
        preload="metadata"
        playsInline
      />
      <Overlay />
      <Flex
        direction="row"
        position="absolute"
        bottom={0}
        w="100%"
        align="center"
        justify="space-between"
        p={{
          mobile: '12px 24px 24px',
          tablet: '12px 24px 24px',
          laptop: '38px 26px 26px',
          desktop: '38px 26px 26px',
        }}
        h={{
          mobile: LIFE_EVENT_IMAGE_SIZE.mobile.descriptionHeight,
          tablet: LIFE_EVENT_IMAGE_SIZE.tablet.descriptionHeight,
          laptop: LIFE_EVENT_IMAGE_SIZE.laptop.descriptionHeight,
          desktop: LIFE_EVENT_IMAGE_SIZE.desktop.descriptionHeight,
        }}
        css={{
          zIndex: 2,
          isolation: 'isolate',
        }}
      >
        <Text
          as="p"
          variant={{
            mobile: 'heading20',
            tablet: 'heading20',
            laptop: 'heading24',
            desktop: 'heading24',
          }}
          weight={600}
          whiteSpace="pre-line"
          align="left"
          css={{
            color: LIFE_EVENT_TEXT,
          }}
        >
          { text }
        </Text>
        <ModalButtonWrapper
          renderModal={({ isOpen, onClose }) => (
            isOpen &&(
              <VideoPreview
                type="video"
                url={url}
                title={text}
                onClose={onClose}
              />
            )
          )}
        >
          { ({ onClick }) => (
            <IconButton
              type="button"
              aria-label={`${text} 재생`}
              aria-haspopup="dialog"
              onClick={() => {
                bucketClickLog({
                  section_id: 'LIFE_EVENT',
                  type: 'button',
                  id: 'PLAY',
                  index,
                  data: {
                    content_title: text,
                  },
                });

                onClick();
              }}
            >
              <IconPlayFilled
                weight="regular"
                size={isMobile ? 24 : 28}
                aspect="width"
                color="white"
              />
            </IconButton>
          ) }
        </ModalButtonWrapper>
      </Flex>
    </Box>
  );
});

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(17, 17, 17, 0.3) 70%,
    rgba(17, 17, 17, 0.5) 100%
  );
  transform: translateZ(0);
  pointer-events: none;
  z-index: 1;
`;

const IconButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(25px);
  border-radius: 50%;
  padding: 20px;

  ${MEDIA_QUERY.min('laptop', css`
    padding: 24px;
  `)}

  > svg {
    transform: translateX(1px);

    ${MEDIA_QUERY.min('laptop', css`
      transform: translateX(2px);
    `)}
  }
`;

const Video = styled.video`
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;

  transform: translateZ(0);
  will-change: transform;
  backface-visibility: hidden;
  z-index: 0;
`;
